/**
 * Authored Electric Spiral Day 1 Journey pages.
 *
 * Story: The Mystery of the Sleeping Wind Engine.
 * Part A uses the actual one-AA Basic Circuit lamp kit.
 * Part B uses the actual two-AA Electric Spiral motor-and-fan kit and PWO 1171.
 * PWO 467 is intentionally not used.
 *
 * This file is data/configuration consumed by JourneyPageRenderer. It does
 * not create bespoke React pages.
 */
const ELECTRIC_SPIRAL_STEP_PAGES = {
  "breeze-station-silent": {
    "step_id": "breeze-station-silent",
    "title": "The Day Breeze Station Fell Silent",
    "subtitle": "Open the Day 1 Emergency Case",
    "phase": "Engage",
    "story": "Breeze Station's welcome beacon is dark and its Wind Engine is silent. Before the new camp engineers can wake the wind, they must prove they can guide invisible power.",
    "blocks": [
      {
        "id": "station-alert",
        "type": "story_hook",
        "eyebrow": "DAY 1 • BREEZE STATION ALERT",
        "title": "Two machines failed before camp began",
        "body": "Do not open either kit yet. Master engineers begin with observations, competing explanations, and safe test ideas—not random rebuilding.",
        "tone": "mystery"
      },
      {
        "id": "station-reveal",
        "type": "image_reveal",
        "title": "Open the Master Engineer's message",
        "prompt": "Reveal the scene and read the apprentice challenge.",
        "reveal_text": "First, your team must restore the small signal beacon. The rule you discover there will be the key to waking the larger Wind Engine.",
        "image": {
          "path": "electric-spiral/step-1/breeze_station_silent.png",
          "alt": "A dark Breeze Station with a silent fan and a challenge note."
        },
        "save_as": "station_revealed"
      },
      {
        "id": "two-kits-gallery",
        "type": "image_gallery",
        "title": "Inspect the two unopened engineering kits",
        "prompt": "Record what the two systems appear to share and what looks different.",
        "save_as": "opening_kit_image",
        "images": [
          {
            "id": "two-kits",
            "label": "Part A and Part B evidence",
            "path": "electric-spiral/step-1/two_kits_evidence.png",
            "caption": "Part A uses one AA battery and a lamp. Part B uses two AA batteries, a motor, and a fan blade. Both need an electrical path.",
            "alt": "A side-by-side view of the basic circuit and Electric Spiral kits."
          }
        ]
      },
      {
        "id": "opening-cause",
        "type": "choice_cards",
        "title": "What could explain both failures?",
        "prompt": "Choose the best current theory. Several causes remain possible.",
        "choices": [
          {
            "id": "open_path",
            "label": "A wire, switch, or contact leaves the path open"
          },
          {
            "id": "battery",
            "label": "The batteries are missing, reversed, or not connected"
          },
          {
            "id": "device",
            "label": "The lamp or motor may not be connected correctly"
          },
          {
            "id": "mechanical",
            "label": "The fan may also have a blade or support problem"
          },
          {
            "id": "need_tests",
            "label": "I need separate electrical and mechanical tests"
          }
        ],
        "save_as": "opening_failure_theory",
        "required": true
      },
      {
        "id": "opening-record",
        "type": "experiment_record",
        "title": "Start the Breeze Station case file",
        "prompt": "Separate visible evidence from your explanation.",
        "save_as": "opening_case_file",
        "fields": [
          {
            "id": "observation_one",
            "label": "Observation 1",
            "type": "text",
            "placeholder": "I can see...",
            "min_length": 8
          },
          {
            "id": "observation_two",
            "label": "Observation 2",
            "type": "text",
            "placeholder": "Another clue is...",
            "min_length": 8
          },
          {
            "id": "initial_theory",
            "label": "Initial theory supported by evidence",
            "type": "text",
            "placeholder": "I think... because...",
            "min_length": 22
          }
        ]
      },
      {
        "id": "step1-saras",
        "type": "saras_checkpoint",
        "title": "Investigate the silent station with Saras",
        "suggested_questions": [
          "What counts as an observation rather than an explanation?",
          "What parts do the two kits seem to share?",
          "How could we test an electrical cause without opening both kits?",
          "Help me make my theory testable."
        ],
        "hidden_context": "The learner is starting The Mystery of the Sleeping Wind Engine. Part A is a one-AA lamp circuit; Part B is a two-AA motor-and-fan circuit. Do not reveal the complete-loop rule or motor reversal yet. Help distinguish observation, explanation, and test. Ask one question at a time.",
        "reveal_policy": "Preserve the mystery. Do not teach the full circuit rule, energy chain, or reversed-wiring answer yet."
      },
      {
        "id": "step1-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Breeze Station case opened",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Your evidence is ready. Continue to the next Breeze Station challenge.",
        "rules": [
          {
            "field": "station_revealed",
            "condition": "truthy"
          },
          {
            "field": "opening_failure_theory",
            "condition": "answered"
          },
          {
            "field": "opening_case_file",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "invisible-power-path": {
    "step_id": "invisible-power-path",
    "title": "Find the Invisible Power Path",
    "subtitle": "The Rule Both Machines Must Obey",
    "phase": "Explore",
    "story": "The first clue says, 'Invisible power only works when it can leave and return.' Before touching a battery, trace the path that a working device needs.",
    "blocks": [
      {
        "id": "path-story",
        "type": "story_hook",
        "eyebrow": "MASTER ENGINEER CLUE 1",
        "title": "Leave and return",
        "body": "A battery can supply energy only when the conducting path is complete through the device and back to the battery. Predict before studying the diagram.",
        "tone": "mystery"
      },
      {
        "id": "gap-prediction",
        "type": "choice_cards",
        "title": "What will a small gap do in this low-voltage kit?",
        "prompt": "Choose before seeing the comparison.",
        "choices": [
          {
            "id": "stop",
            "label": "It will interrupt current and the device will stop"
          },
          {
            "id": "jump",
            "label": "Current will jump the gap easily"
          },
          {
            "id": "brighter",
            "label": "The gap will make the device brighter or faster"
          },
          {
            "id": "not_sure",
            "label": "I need diagram evidence"
          }
        ],
        "save_as": "gap_prediction",
        "required": true
      },
      {
        "id": "path-gallery",
        "type": "image_gallery",
        "title": "Compare open and closed paths",
        "prompt": "Switch between the diagrams and trace every connection.",
        "save_as": "path_evidence_image",
        "images": [
          {
            "id": "loop",
            "label": "Complete loop map",
            "path": "electric-spiral/step-2/complete_loop_map.png",
            "caption": "The path goes from the battery through the switch and device, then returns to the battery.",
            "alt": "A battery, switch, device, and return-path diagram."
          },
          {
            "id": "open-closed",
            "label": "Open versus closed",
            "path": "electric-spiral/step-2/open_closed_comparison.png",
            "caption": "An open switch leaves a gap. A closed switch joins the conducting path.",
            "alt": "Comparison of open and closed circuits."
          }
        ]
      },
      {
        "id": "loop-sequence",
        "type": "sequence_builder",
        "title": "Build the complete path in order",
        "prompt": "Arrange the path from the battery, through the working device, and back.",
        "save_as": "complete_path_sequence",
        "items": [
          {
            "id": "battery_start",
            "label": "Battery terminal supplies the push"
          },
          {
            "id": "outgoing_wire",
            "label": "Outgoing conducting wire"
          },
          {
            "id": "closed_switch",
            "label": "Closed switch/contact"
          },
          {
            "id": "device",
            "label": "Lamp or motor"
          },
          {
            "id": "return_wire",
            "label": "Return conducting wire"
          },
          {
            "id": "battery_return",
            "label": "Other battery terminal"
          }
        ],
        "correct_order": [
          "battery_start",
          "outgoing_wire",
          "closed_switch",
          "device",
          "return_wire",
          "battery_return"
        ],
        "correct_feedback": "Correct. The device sits inside a complete conducting loop.",
        "retry_feedback": "Find the gap: current must pass through the device and return to the battery."
      },
      {
        "id": "switch-check",
        "type": "quiz_question",
        "title": "Apprentice circuit check",
        "prompt": "The battery and lamp are connected, but the switch is open. What is the best explanation for a dark lamp?",
        "choices": [
          {
            "id": "path_open",
            "label": "The switch leaves the conducting path incomplete"
          },
          {
            "id": "battery_empty",
            "label": "An open switch always empties the battery"
          },
          {
            "id": "lamp_too_small",
            "label": "The lamp is too small for electricity"
          },
          {
            "id": "air_block",
            "label": "Air blocks light from leaving"
          }
        ],
        "correct_answer": "path_open",
        "save_as": "switch_path_check",
        "correct_feedback": "Correct. The open switch creates a gap in this low-voltage path.",
        "incorrect_feedback": "Trace the conducting loop and look for the interruption."
      },
      {
        "id": "loop-explanation",
        "type": "evidence_response",
        "title": "Write the hidden rule in your own words",
        "prompt": "Explain why the lamp and motor can both fail when a single connection is open.",
        "placeholder": "Both devices need... If the path has a gap...",
        "save_as": "complete_loop_explanation",
        "min_length": 35,
        "required": true
      },
      {
        "id": "step2-saras",
        "type": "saras_checkpoint",
        "title": "Trace the invisible path with Saras",
        "suggested_questions": [
          "Ask me to trace the path one connection at a time.",
          "Why does an open switch stop the device?",
          "What is the difference between current path and energy transfer?",
          "Challenge my complete-loop explanation."
        ],
        "hidden_context": "The learner is deriving the complete-loop rule for a one-AA lamp circuit and a two-AA motor circuit. Use age-appropriate language: a closed conducting loop allows current; the battery transfers energy electrically. Do not say energy returns to the battery or electricity is used up.",
        "reveal_policy": "The complete-loop rule may now be taught. Do not reveal Part B motor direction reversal."
      },
      {
        "id": "step2-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Invisible power path decoded",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Your evidence is ready. Continue to the next Breeze Station challenge.",
        "rules": [
          {
            "field": "gap_prediction",
            "condition": "answered"
          },
          {
            "field": "complete_path_sequence",
            "condition": "sequence_correct"
          },
          {
            "field": "switch_path_check",
            "condition": "equals",
            "value": "path_open"
          },
          {
            "field": "complete_loop_explanation",
            "condition": "min_length",
            "value": 35
          }
        ]
      }
    ]
  },
  "light-the-signal-beacon": {
    "step_id": "light-the-signal-beacon",
    "title": "Part A: Light the Signal Beacon",
    "subtitle": "The Apprentice Circuit Test",
    "phase": "Build",
    "story": "The signal beacon is the apprentice test. Build one careful circuit with the one-AA holder, switch, lamp module, and metal-to-metal connections.",
    "blocks": [
      {
        "id": "parta-story",
        "type": "story_hook",
        "eyebrow": "PART A • APPRENTICE TEST",
        "title": "Restore one small light before waking the wind",
        "body": "Open only the Basic Circuit kit. Keep the Electric Spiral kit closed until Part A evidence is complete.",
        "tone": "mystery"
      },
      {
        "id": "parta-gallery",
        "type": "image_gallery",
        "title": "Study the exact Part A kit and guide",
        "prompt": "Use the supplied guide—not a generic circuit picture.",
        "save_as": "parta_guide_examined",
        "images": [
          {
            "id": "parta-kit",
            "label": "Actual Part A kit",
            "path": "electric-spiral/step-3/basic_circuit_actual_kit.png",
            "caption": "The physical kit uses one AA battery, a holder, a separate switch, red/black leads, a lamp-holder module, and a guide card.",
            "alt": "The actual Basic Circuit Part A kit and instruction card."
          },
          {
            "id": "parta-guide",
            "label": "Build guide",
            "path": "electric-spiral/step-3/basic_circuit_build_guide.png",
            "caption": "Follow the supplied lead orientation and make clean metal-to-metal contacts.",
            "alt": "The Basic Circuit assembly instructions."
          }
        ]
      },
      {
        "id": "parta-readiness",
        "type": "build_checklist",
        "title": "Part A power-off readiness check",
        "prompt": "Mark each item complete only after the teacher releases the kit.",
        "save_as": "parta_readiness",
        "statuses": [
          {
            "id": "complete",
            "label": "Checked"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "one_aa",
            "label": "Exactly one teacher-approved AA battery is ready",
            "help": "Do not insert it until the holder and switch are identified."
          },
          {
            "id": "no_short",
            "label": "Red and black leads will never be connected directly",
            "help": "A direct connection can overheat the battery or wire."
          },
          {
            "id": "metal_contact",
            "label": "Alligator clips will touch exposed metal, not plastic insulation"
          },
          {
            "id": "lamp_orientation",
            "label": "The supplied lamp-holder lead orientation will be followed",
            "help": "Long holder lead to red; short holder lead to black, as shown in this kit guide."
          },
          {
            "id": "cutting",
            "label": "Adhesive pieces are teacher-prepared or cutting is supervised"
          }
        ]
      },
      {
        "id": "parta-build",
        "type": "build_checklist",
        "title": "Build the signal beacon",
        "prompt": "Work with the switch open. Mark each step only when the connection is secure.",
        "save_as": "parta_build_checklist",
        "statuses": [
          {
            "id": "complete",
            "label": "Completed"
          },
          {
            "id": "help",
            "label": "Need help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "mount_holder",
            "label": "Mount the battery holder and switch on the guide card"
          },
          {
            "id": "connect_red",
            "label": "Connect the red path to the long lamp-holder lead"
          },
          {
            "id": "connect_black",
            "label": "Connect the black path to the short lamp-holder lead"
          },
          {
            "id": "inspect_contacts",
            "label": "Check every metal-to-metal contact"
          },
          {
            "id": "insert_battery",
            "label": "Insert one AA battery with the shown polarity"
          },
          {
            "id": "close_switch",
            "label": "Close the switch only when the teacher says the circuit is ready"
          }
        ]
      },
      {
        "id": "parta-trial",
        "type": "experiment_record",
        "title": "Record the signal-beacon test",
        "prompt": "A dark lamp is still useful evidence. Diagnose one cause at a time.",
        "save_as": "parta_trial",
        "fields": [
          {
            "id": "first_result",
            "label": "First switch test",
            "type": "choice",
            "choices": [
              {
                "id": "lit",
                "label": "Lamp lit"
              },
              {
                "id": "dark",
                "label": "Lamp stayed dark"
              },
              {
                "id": "flicker",
                "label": "Lamp flickered"
              }
            ]
          },
          {
            "id": "evidence",
            "label": "What did you observe?",
            "type": "text",
            "placeholder": "When the switch closed...",
            "min_length": 18
          },
          {
            "id": "fix",
            "label": "If needed, what single check or fix did you make?",
            "type": "text",
            "placeholder": "I checked...",
            "min_length": 12
          }
        ]
      },
      {
        "id": "parta-check",
        "type": "quiz_question",
        "title": "Signal-beacon diagnostic",
        "prompt": "The battery is inserted and the switch is closed, but one alligator clip grips plastic insulation instead of bare metal. What should the team do first?",
        "choices": [
          {
            "id": "move_to_metal",
            "label": "Open the switch, move the clip to exposed metal, and retest"
          },
          {
            "id": "add_battery",
            "label": "Add a second battery"
          },
          {
            "id": "touch_wires",
            "label": "Touch red and black wires together"
          },
          {
            "id": "shake",
            "label": "Shake the lamp while powered"
          }
        ],
        "correct_answer": "move_to_metal",
        "save_as": "parta_contact_check",
        "correct_feedback": "Correct. Restore metal-to-metal contact with power off, then retest.",
        "incorrect_feedback": "Use the safest one-variable check. Never short the battery leads."
      },
      {
        "id": "step3-saras",
        "type": "saras_checkpoint",
        "title": "Coach the signal-beacon build with Saras",
        "suggested_questions": [
          "Give me one safe diagnostic check at a time.",
          "How can I tell whether the switch or a contact is the problem?",
          "Why must clips touch exposed metal?",
          "Question my evidence without rebuilding it for me."
        ],
        "hidden_context": "The learner is building the physical one-AA Basic Circuit Part A kit. Exact guide: separate switch; red path to long lamp-holder lead; black path to short lead. Safety: never connect red and black directly; power off before moving clips; teacher supervises adhesive cutting. Give one check at a time and defer physical safety decisions to the teacher.",
        "reveal_policy": "Part A complete-loop and contact science may be discussed. Do not jump ahead to Part B wiring reversal."
      },
      {
        "id": "step3-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Signal beacon restored",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "The apprentice test is complete. The Wind Engine is now unlocked.",
        "rules": [
          {
            "field": "parta_guide_examined",
            "condition": "answered"
          },
          {
            "field": "parta_readiness",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "parta_build_checklist",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "parta_trial",
            "condition": "record_complete"
          },
          {
            "field": "parta_contact_check",
            "condition": "equals",
            "value": "move_to_metal"
          }
        ]
      }
    ]
  },
  "one-loop-two-jobs": {
    "step_id": "one-loop-two-jobs",
    "title": "One Power Loop, Two Different Jobs",
    "subtitle": "From Light to Motion",
    "phase": "Explain",
    "story": "The signal beacon is awake. Now compare it with the sleeping Wind Engine: the hidden circuit rule remains, but the output device changes from a lamp to a motor.",
    "instructional_flow": {
      "summary": [
        "I_DO",
        "WE_DO",
        "YOU_DO"
      ],
      "teacher_goal": "Model how to compare the two systems, jointly reconstruct the familiar lamp pathway, and then release students to predict and explain the Wind Engine pathway."
    },
    "blocks": [
      {
        "id": "bridge-story",
        "type": "story_hook",
        "instructional_mode": "I_DO",
        "grouping": "whole_class",
        "teacher_cue": "Read the Master Engineer clue aloud. Frame the comparison, but do not list the similarities and differences.",
        "student_cue": "Listen, observe, and make a quiet prediction about what stays the same.",
        "eyebrow": "MASTER ENGINEER CLUE 2",
        "title": "What stays the same when the output changes?",
        "body": "Predict the similarities and differences before watching any explanation.",
        "tone": "mystery"
      },
      {
        "id": "same-different",
        "type": "classification",
        "instructional_mode": "WE_DO",
        "grouping": "teacher_choice",
        "teacher_cue": "Classify one example together, then ask students to justify the remaining choices with Part A evidence.",
        "student_cue": "Discuss each statement and explain why it belongs to Part A, Part B, or both.",
        "title": "Classify the two systems",
        "prompt": "For each statement, decide whether it describes Part A, Part B, or both.",
        "categories": [
          {
            "id": "part_a",
            "label": "Part A only"
          },
          {
            "id": "part_b",
            "label": "Part B only"
          },
          {
            "id": "both",
            "label": "Both"
          }
        ],
        "items": [
          {
            "id": "one_aa",
            "label": "Uses one AA battery"
          },
          {
            "id": "two_aa",
            "label": "Uses two AA batteries"
          },
          {
            "id": "closed_loop",
            "label": "Needs a complete conducting loop"
          },
          {
            "id": "switch",
            "label": "Uses a switch or contact to open/close the path"
          },
          {
            "id": "light",
            "label": "Main output is light"
          },
          {
            "id": "rotation",
            "label": "Main output begins with motor rotation"
          },
          {
            "id": "metal_contacts",
            "label": "Needs clean metal-to-metal contacts"
          }
        ],
        "save_as": "part_comparison",
        "required": true
      },
      {
        "id": "comparison-gallery",
        "type": "image_gallery",
        "instructional_mode": "WE_DO",
        "grouping": "whole_class",
        "teacher_cue": "Use the diagrams to check one earlier answer. Ask students what visible evidence supports a revision.",
        "student_cue": "Study the diagrams together and revise the classification when evidence requires it.",
        "title": "Study the two-machine comparison",
        "prompt": "Use the diagrams to revise your classification.",
        "save_as": "comparison_image",
        "images": [
          {
            "id": "jobs",
            "label": "One loop, two jobs",
            "path": "electric-spiral/step-4/one_loop_two_jobs.png",
            "caption": "Both systems need a battery, a closed path, and a device. Their output devices do different work.",
            "alt": "A side-by-side comparison of lamp and motor circuits."
          },
          {
            "id": "energy",
            "label": "Two energy chains",
            "path": "electric-spiral/step-4/two_energy_chains.png",
            "caption": "Battery energy is transferred electrically. The lamp produces light; the motor produces rotation and the blade moves air.",
            "alt": "Energy chains for the lamp and Wind Engine."
          }
        ]
      },
      {
        "id": "parta-chain",
        "type": "sequence_builder",
        "instructional_mode": "WE_DO",
        "grouping": "whole_class",
        "teacher_cue": "Rebuild the familiar Part A energy chain together. Emphasize that this is an energy chain, not the order of wires.",
        "student_cue": "Help reconstruct how stored battery energy leads to light and some heat.",
        "title": "Rebuild the Part A energy chain",
        "prompt": "Order the transformations, not the wire path.",
        "save_as": "parta_energy_chain",
        "items": [
          {
            "id": "chemical",
            "label": "Stored chemical energy in the AA battery"
          },
          {
            "id": "electrical",
            "label": "Energy transferred electrically through the circuit"
          },
          {
            "id": "lamp",
            "label": "Lamp module receives electrical input"
          },
          {
            "id": "light",
            "label": "Light and some heat are produced"
          }
        ],
        "correct_order": [
          "chemical",
          "electrical",
          "lamp",
          "light"
        ],
        "correct_feedback": "Correct. This is the energy chain, not a list of wire locations.",
        "retry_feedback": "Start with stored energy in the battery and end with the lamp's outputs."
      },
      {
        "id": "partb-predicted-chain",
        "type": "sequence_builder",
        "instructional_mode": "YOU_DO",
        "grouping": "teacher_choice",
        "teacher_cue": "Release students to transfer the Part A pattern. Prompt only with where the chain begins and what the motor does.",
        "student_cue": "Predict the Part B energy chain using the Part A example as evidence.",
        "title": "Predict the Part B energy chain",
        "prompt": "What should happen after electrical transfer reaches the motor?",
        "save_as": "partb_predicted_chain",
        "items": [
          {
            "id": "chemical2",
            "label": "Stored chemical energy in two AA batteries"
          },
          {
            "id": "electrical2",
            "label": "Energy transferred electrically through a closed circuit"
          },
          {
            "id": "rotation",
            "label": "DC motor shaft rotates"
          },
          {
            "id": "air",
            "label": "Angled blade pushes and moves room air"
          }
        ],
        "correct_order": [
          "chemical2",
          "electrical2",
          "rotation",
          "air"
        ],
        "correct_feedback": "Correct. The fan moves air that is already present.",
        "retry_feedback": "The motor must rotate before the attached blade can move air."
      },
      {
        "id": "power-loop-video",
        "type": "video_asset",
        "instructional_mode": "WE_DO",
        "grouping": "whole_class",
        "teacher_cue": "Use the optional video after students commit to their prediction. Pause at the complete-loop and output-device comparisons.",
        "student_cue": "Watch for evidence that confirms or changes the predicted Part B chain.",
        "title": "Optional evidence video: The Power Loop",
        "mission": "Watch after your comparison. Focus on the complete loop and the change from a light output to a motor output. The video contains ideas that you will test physically.",
        "save_as": "power_loop_video_opened",
        "asset": {
          "id": "power-loop-video-asset",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7052,
            "tag_id": 30069
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 169,
            "collection_item_id": 148
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 936,
            "source_topic_id": 961
          }
        }
      },
      {
        "id": "bridge-explanation",
        "type": "evidence_response",
        "instructional_mode": "YOU_DO",
        "grouping": "teacher_choice",
        "teacher_cue": "Require one similarity and one difference. Ask for evidence rather than supplying the wording.",
        "student_cue": "Explain the shared circuit rule and the different jobs of the lamp and motor.",
        "title": "State the bridge rule",
        "prompt": "Explain one important similarity and one important difference between Parts A and B.",
        "placeholder": "Both systems... Part A... Part B...",
        "save_as": "part_bridge_explanation",
        "min_length": 45,
        "required": true
      },
      {
        "id": "step4-saras",
        "type": "saras_checkpoint",
        "instructional_mode": "YOU_DO",
        "grouping": "teacher_choice",
        "teacher_cue": "Students may use Saras to test their reasoning. Saras should challenge rather than write the explanation.",
        "student_cue": "Ask Saras to question your reasoning, then revise it in your own words.",
        "title": "Compare light and motion with Saras",
        "suggested_questions": [
          "Ask me what stays the same between the two circuits.",
          "Check whether I confused current path with energy transformation.",
          "Why does the fan move existing air rather than create air?",
          "Challenge my Part A to Part B bridge explanation."
        ],
        "hidden_context": "The learner completed a one-AA lamp circuit and is predicting a two-AA motor-and-fan circuit. Keep the complete conducting loop distinct from energy transformation. Battery chemical energy is transferred electrically; lamp produces light; motor produces rotation; blade moves existing air. Do not reveal reversed-wiring direction before Step 8.",
        "reveal_policy": "The lamp-to-motor comparison may be taught. Preserve the backward-breeze mystery."
      },
      {
        "id": "step4-completion",
        "type": "completion_gate",
        "instructional_mode": "YOU_DO",
        "grouping": "teacher_choice",
        "teacher_cue": "Have students inspect their own progress and complete only the missing evidence.",
        "student_cue": "Check your progress and finish the activities that remain incomplete.",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Wind Engine principle predicted",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Your evidence is ready. Continue to the next Breeze Station challenge.",
        "rules": [
          {
            "field": "part_comparison",
            "condition": "completed"
          },
          {
            "field": "parta_energy_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "partb_predicted_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "part_bridge_explanation",
            "condition": "min_length",
            "value": 45
          }
        ]
      }
    ]
  },
  "meet-the-wind-engine": {
    "step_id": "meet-the-wind-engine",
    "title": "Part B: Meet the Sleeping Wind Engine",
    "subtitle": "Virtual Component Investigation",
    "phase": "Explore",
    "story": "The larger machine is unlocked, but the physical kit stays closed while the team identifies each component and predicts its job in the system.",
    "blocks": [
      {
        "id": "partb-reveal-story",
        "type": "story_hook",
        "eyebrow": "PART B • WIND ENGINE UNLOCKED",
        "title": "Name the jobs before touching the parts",
        "body": "Strong builders do not treat every piece as decoration. Each component either supplies energy, completes the circuit, creates rotation, moves air, or stabilizes the structure.",
        "tone": "mystery"
      },
      {
        "id": "partb-mainmenu",
        "type": "pwo_asset",
        "title": "Enter the Sleeping Wind Engine",
        "mission": "Open the main menu. Observe the completed machine and identify the battery holder, motor, blade, and supporting structure without beginning assembly.",
        "save_as": "partb_mainmenu_opened",
        "asset": {
          "id": "partb-mainmenu-asset",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1171,
            "scene_name": "mainmenu"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 169,
            "collection_item_id": 148
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 936,
            "source_topic_id": 961
          }
        }
      },
      {
        "id": "partb-materials",
        "type": "pwo_asset",
        "title": "Inspect the virtual material tray",
        "mission": "Open Material Selection. Click the required parts and compare the virtual labels with the physical kit image.",
        "save_as": "partb_materials_opened",
        "asset": {
          "id": "partb-materials-asset",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1171,
            "scene_name": "material_selection"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 169,
            "collection_item_id": 148
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 936,
            "source_topic_id": 961
          }
        }
      },
      {
        "id": "partb-gallery",
        "type": "image_gallery",
        "title": "Compare virtual and physical evidence",
        "prompt": "The PWO is a model; the physical pieces may differ in color or shape.",
        "save_as": "partb_component_image",
        "images": [
          {
            "id": "mainmenu",
            "label": "PWO completed Wind Engine",
            "path": "electric-spiral/step-5/electric_spiral_mainmenu.png",
            "caption": "The virtual model shows the final relationship among the battery holder, stand, motor, and blade.",
            "alt": "Electric Spiral PWO main menu."
          },
          {
            "id": "materials",
            "label": "PWO materials",
            "path": "electric-spiral/step-5/electric_spiral_material_selection.png",
            "caption": "Use the PWO to identify component roles before opening the physical kit.",
            "alt": "Electric Spiral PWO material selection scene."
          },
          {
            "id": "actual",
            "label": "Actual Part B kit",
            "path": "electric-spiral/step-5/electric_spiral_actual_kit.png",
            "caption": "The actual kit includes the base card, supports, foam, fittings, motor, fixed blade, wires, and a holder for two AA batteries.",
            "alt": "Actual Electric Spiral physical kit components."
          }
        ]
      },
      {
        "id": "component-jobs",
        "type": "matching",
        "title": "Match each component to its main job",
        "prompt": "Choose function before memorizing vocabulary.",
        "save_as": "component_job_map",
        "items": [
          {
            "id": "battery_holder",
            "label": "Two-AA battery holder",
            "options": [
              {
                "id": "energy",
                "label": "Holds the energy source and provides circuit contacts"
              },
              {
                "id": "air",
                "label": "Directly creates air"
              },
              {
                "id": "stability",
                "label": "Only decorates the base"
              }
            ]
          },
          {
            "id": "motor",
            "label": "DC motor",
            "options": [
              {
                "id": "rotation",
                "label": "Changes electrical input into shaft rotation"
              },
              {
                "id": "light",
                "label": "Produces the signal light"
              },
              {
                "id": "battery",
                "label": "Stores the AA batteries"
              }
            ]
          },
          {
            "id": "blade",
            "label": "Fixed angled fan blade",
            "options": [
              {
                "id": "move_air",
                "label": "Pushes and moves air when rotating"
              },
              {
                "id": "make_energy",
                "label": "Creates battery energy"
              },
              {
                "id": "switch",
                "label": "Opens the electrical circuit"
              }
            ]
          },
          {
            "id": "stand",
            "label": "Base, supports, fittings, and foam",
            "options": [
              {
                "id": "stable",
                "label": "Hold the motor and blade in a stable alignment"
              },
              {
                "id": "conduct",
                "label": "Replace all electrical wires"
              },
              {
                "id": "reverse",
                "label": "Reverse battery chemistry"
              }
            ]
          },
          {
            "id": "leads",
            "label": "Red and black leads",
            "options": [
              {
                "id": "path",
                "label": "Provide conducting paths to the motor terminals"
              },
              {
                "id": "air2",
                "label": "Guide airflow like ducts"
              },
              {
                "id": "glue",
                "label": "Glue the blade in place"
              }
            ]
          }
        ]
      },
      {
        "id": "component-explanation",
        "type": "evidence_response",
        "title": "Predict the machine's weakest point",
        "prompt": "Which component or connection would you inspect first if the motor had power but the blade wobbled? Explain why.",
        "placeholder": "I would inspect... because...",
        "save_as": "predicted_weak_point",
        "min_length": 30,
        "required": true
      },
      {
        "id": "step5-saras",
        "type": "saras_checkpoint",
        "title": "Investigate component jobs with Saras",
        "suggested_questions": [
          "Quiz me on one component job at a time.",
          "Why are the supports part of the engineering, not decoration?",
          "What evidence would separate a motor problem from a blade problem?",
          "Check my weak-point prediction."
        ],
        "hidden_context": "The learner is using PWO 1171 mainmenu and material_selection for the Electric Spiral physical kit. Exact physical source: two AA batteries, holder with metal lever/contact, red/black leads, DC motor, fixed fan blade, base/card, supports/fittings, foam. Emphasize function and model-versus-real differences. Do not reveal Step 8 reversal result.",
        "reveal_policy": "Component roles may be explained. Keep physical assembly under teacher control."
      },
      {
        "id": "step5-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Wind Engine components decoded",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Your evidence is ready. Continue to the next Breeze Station challenge.",
        "rules": [
          {
            "field": "partb_mainmenu_opened",
            "condition": "truthy"
          },
          {
            "field": "partb_materials_opened",
            "condition": "truthy"
          },
          {
            "field": "component_job_map",
            "condition": "completed"
          },
          {
            "field": "predicted_weak_point",
            "condition": "min_length",
            "value": 30
          }
        ]
      }
    ]
  },
  "build-the-breeze-tower": {
    "step_id": "build-the-breeze-tower",
    "title": "Build the Breeze Tower",
    "subtitle": "Safe Part B Assembly",
    "phase": "Build",
    "story": "The Wind Engine needs more than a correct circuit. A loose motor, off-center blade, or unstable frame can waste the team's electrical success.",
    "blocks": [
      {
        "id": "tower-story",
        "type": "story_hook",
        "eyebrow": "MASTER ENGINEER CLUE 3",
        "title": "A working circuit needs a stable machine",
        "body": "Open the Part B kit only after the teacher approves your component inventory. Keep batteries out—or the holder contact open—during assembly.",
        "tone": "mystery"
      },
      {
        "id": "assembly-gallery",
        "type": "image_gallery",
        "title": "Use both physical and virtual assembly evidence",
        "prompt": "The printed guide is the authority for the physical kit. The PWO is a visual companion.",
        "save_as": "assembly_image_examined",
        "images": [
          {
            "id": "instructions",
            "label": "Physical instructions",
            "path": "electric-spiral/step-6/electric_spiral_instructions.png",
            "caption": "Follow the actual kit order and warnings. The guide uses a two-AA holder and motor terminal tabs.",
            "alt": "Electric Spiral physical kit instructions."
          },
          {
            "id": "overview",
            "label": "PWO assembly overview",
            "path": "electric-spiral/step-6/assembly_overview.png",
            "caption": "The PWO experiment scene contains six internal assembly buttons.",
            "alt": "Electric Spiral PWO experiment scene."
          },
          {
            "id": "s1",
            "label": "Virtual assembly step 1",
            "path": "electric-spiral/step-6/assembly_step_1.png",
            "caption": "Use as a visual comparison, not a replacement for the physical guide.",
            "alt": "PWO assembly step 1."
          },
          {
            "id": "s2",
            "label": "Virtual assembly step 2",
            "path": "electric-spiral/step-6/assembly_step_2.png",
            "caption": "Confirm the supports are fully seated and stable.",
            "alt": "PWO assembly step 2."
          },
          {
            "id": "s3",
            "label": "Virtual assembly step 3",
            "path": "electric-spiral/step-6/assembly_step_3.png",
            "caption": "Check motor placement and support alignment.",
            "alt": "PWO assembly step 3."
          },
          {
            "id": "s4",
            "label": "Virtual assembly step 4",
            "path": "electric-spiral/step-6/assembly_step_4.png",
            "caption": "Keep power off while attaching or adjusting the leads.",
            "alt": "PWO assembly step 4."
          },
          {
            "id": "s5",
            "label": "Virtual assembly step 5",
            "path": "electric-spiral/step-6/assembly_step_5.png",
            "caption": "The blade must fit securely and remain clear of the structure.",
            "alt": "PWO assembly step 5."
          },
          {
            "id": "s6",
            "label": "Virtual assembly step 6",
            "path": "electric-spiral/step-6/assembly_step_6.png",
            "caption": "Inspect the completed structure before inserting batteries.",
            "alt": "PWO assembly step 6."
          }
        ]
      },
      {
        "id": "assembly-pwo",
        "type": "pwo_asset",
        "title": "Open the six-step virtual assembly guide",
        "mission": "Use the internal step buttons to preview and check each stage. Return to the Journey before physical power testing.",
        "save_as": "assembly_pwo_opened",
        "asset": {
          "id": "assembly-pwo-asset",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1171,
            "scene_name": "exeprimentscene"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 169,
            "collection_item_id": 148
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 936,
            "source_topic_id": 961
          }
        }
      },
      {
        "id": "partb-safety",
        "type": "build_checklist",
        "title": "Part B power-off safety gate",
        "prompt": "Every item must be checked before assembly proceeds.",
        "save_as": "partb_safety_gate",
        "statuses": [
          {
            "id": "complete",
            "label": "Checked"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "batteries_out",
            "label": "Batteries are out or the holder contact is open while assembling"
          },
          {
            "id": "no_direct_short",
            "label": "Red and black leads will never be connected directly"
          },
          {
            "id": "motor_tabs",
            "label": "Leads will contact the motor's terminal tabs—not its metal casing"
          },
          {
            "id": "blade_zone",
            "label": "Hair, fingers, and loose clothing will stay away from the blade"
          },
          {
            "id": "power_off_adjust",
            "label": "Power will be off before moving wires or reseating the blade"
          }
        ]
      },
      {
        "id": "partb-build",
        "type": "build_checklist",
        "title": "Assemble the Breeze Tower",
        "prompt": "Mark one step at a time. A teacher should inspect before batteries are inserted.",
        "save_as": "partb_build_checklist",
        "statuses": [
          {
            "id": "complete",
            "label": "Completed"
          },
          {
            "id": "help",
            "label": "Need help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "base",
            "label": "Prepare the base/card and support pieces"
          },
          {
            "id": "supports",
            "label": "Seat fittings and supports firmly so the tower does not lean"
          },
          {
            "id": "motor",
            "label": "Mount the motor securely in the correct orientation"
          },
          {
            "id": "holder",
            "label": "Mount the two-AA battery holder without pinching wires"
          },
          {
            "id": "leads",
            "label": "Attach red and black leads to the motor terminal tabs"
          },
          {
            "id": "blade",
            "label": "Press the fixed blade securely and check that it spins freely by visual inspection only"
          },
          {
            "id": "inspection",
            "label": "Teacher inspects stability, clearance, and wiring before power"
          }
        ]
      },
      {
        "id": "assembly-risk",
        "type": "choice_cards",
        "title": "Which issue is mainly mechanical?",
        "prompt": "Choose the best answer before testing power.",
        "choices": [
          {
            "id": "off_center_blade",
            "label": "The blade is loose or off-center and may wobble"
          },
          {
            "id": "open_switch",
            "label": "The holder contact is open"
          },
          {
            "id": "reversed_battery",
            "label": "A battery is inserted with the wrong polarity"
          },
          {
            "id": "poor_contact",
            "label": "A lead is not touching a motor terminal"
          }
        ],
        "save_as": "mechanical_risk_prediction",
        "required": true
      },
      {
        "id": "assembly-note",
        "type": "evidence_response",
        "title": "Record the predicted weak point",
        "prompt": "Where might this specific build become unstable or lose contact?",
        "placeholder": "The weak point may be... I will check it by...",
        "save_as": "assembly_weak_point",
        "min_length": 35,
        "required": true
      },
      {
        "id": "step6-saras",
        "type": "saras_checkpoint",
        "title": "Coach the Breeze Tower assembly with Saras",
        "suggested_questions": [
          "Ask me one safety check before each build stage.",
          "How can I distinguish an electrical connection from structural support?",
          "Why must the lead touch a motor terminal rather than the casing?",
          "Challenge my predicted weak point."
        ],
        "hidden_context": "The learner is physically assembling the two-AA Electric Spiral kit while using PWO 1171 exeprimentscene. Printed physical instructions are authoritative if visual order differs. Safety: batteries out/contact open, no direct short, leads on terminal tabs not casing, power off before adjustments, blade clearance. Defer hands-on decisions to teacher.",
        "reveal_policy": "Assembly concepts may be discussed. Do not direct powered adjustments or reveal the reversal result."
      },
      {
        "id": "step6-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Breeze Tower ready for inspection",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "The structure is ready. Wait for teacher approval before the first powered test.",
        "rules": [
          {
            "field": "assembly_image_examined",
            "condition": "answered"
          },
          {
            "field": "assembly_pwo_opened",
            "condition": "truthy"
          },
          {
            "field": "partb_safety_gate",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "partb_build_checklist",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "mechanical_risk_prediction",
            "condition": "equals",
            "value": "off_center_blade"
          },
          {
            "field": "assembly_weak_point",
            "condition": "min_length",
            "value": 35
          }
        ]
      }
    ]
  },
  "wake-the-wind-engine": {
    "step_id": "wake-the-wind-engine",
    "title": "Wake the Wind Engine",
    "subtitle": "First Power and Airflow Test",
    "phase": "Investigate",
    "story": "The tower is assembled. Now close the circuit and collect three kinds of evidence: what you see, what you hear, and what the moving air does.",
    "blocks": [
      {
        "id": "wake-story",
        "type": "story_hook",
        "eyebrow": "FIRST POWER TEST",
        "title": "A spinning blade is only one piece of evidence",
        "body": "Use the same safe tissue or ribbon test position throughout the investigation. Never place fingers near the blade.",
        "tone": "mystery"
      },
      {
        "id": "test-gallery",
        "type": "image_gallery",
        "title": "Plan the first wind test",
        "prompt": "Study the evidence plan before inserting batteries.",
        "save_as": "first_test_image",
        "images": [
          {
            "id": "test-plan",
            "label": "Three kinds of evidence",
            "path": "electric-spiral/step-7/first_wind_test.png",
            "caption": "Observe blade rotation, listen for the motor, and use tissue/ribbon movement to show airflow from a safe distance.",
            "alt": "A safe first wind test using visual, sound, and ribbon evidence."
          },
          {
            "id": "learned",
            "label": "PWO science summary",
            "path": "electric-spiral/step-7/electric_spiral_i_learned.png",
            "caption": "The PWO summary connects the closed circuit, battery, motor rotation, fan blade, and moving air.",
            "alt": "Electric Spiral PWO I Learned screen."
          }
        ]
      },
      {
        "id": "first-run-prediction",
        "type": "choice_cards",
        "title": "What should happen when the circuit closes?",
        "prompt": "Choose the most complete causal prediction.",
        "choices": [
          {
            "id": "motor_then_air",
            "label": "Current can pass through the motor; the shaft rotates; the angled blade moves air"
          },
          {
            "id": "air_first",
            "label": "Air starts moving first and then creates electricity"
          },
          {
            "id": "battery_spins",
            "label": "The batteries themselves spin the blade directly"
          },
          {
            "id": "fan_creates_air",
            "label": "The fan creates new air inside the room"
          }
        ],
        "save_as": "first_run_prediction",
        "required": true
      },
      {
        "id": "first-run-checklist",
        "type": "build_checklist",
        "title": "Teacher-approved first power sequence",
        "prompt": "Do not skip the final clearance check.",
        "save_as": "first_run_checklist",
        "statuses": [
          {
            "id": "complete",
            "label": "Completed"
          },
          {
            "id": "help",
            "label": "Need help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "inspect",
            "label": "Teacher confirms stable structure, blade clearance, and terminal contacts"
          },
          {
            "id": "insert_two",
            "label": "Insert exactly two AA batteries with the shown polarity"
          },
          {
            "id": "place_indicator",
            "label": "Place tissue/ribbon at the agreed safe test location"
          },
          {
            "id": "clear_zone",
            "label": "Clear fingers, hair, and loose clothing from the blade zone"
          },
          {
            "id": "close_contact",
            "label": "Close the holder contact/switch for a brief first test"
          },
          {
            "id": "open_after",
            "label": "Open the contact after observations are recorded"
          }
        ]
      },
      {
        "id": "first-run-record",
        "type": "experiment_record",
        "title": "Record three kinds of evidence",
        "prompt": "A negative result is acceptable when it is recorded honestly.",
        "save_as": "first_run_record",
        "fields": [
          {
            "id": "visual",
            "label": "What did you see?",
            "type": "choice",
            "choices": [
              {
                "id": "steady",
                "label": "Blade spun steadily"
              },
              {
                "id": "wobble",
                "label": "Blade spun but wobbled"
              },
              {
                "id": "still",
                "label": "Blade did not spin"
              }
            ]
          },
          {
            "id": "sound",
            "label": "What did you hear?",
            "type": "choice",
            "choices": [
              {
                "id": "steady_hum",
                "label": "Steady motor sound"
              },
              {
                "id": "intermittent",
                "label": "Intermittent sound"
              },
              {
                "id": "silent",
                "label": "No motor sound"
              }
            ]
          },
          {
            "id": "air",
            "label": "What happened to the tissue/ribbon?",
            "type": "choice",
            "choices": [
              {
                "id": "away",
                "label": "Moved away from the blade side tested"
              },
              {
                "id": "toward",
                "label": "Moved toward the machine / strongest air seemed opposite"
              },
              {
                "id": "none",
                "label": "No clear movement"
              }
            ]
          },
          {
            "id": "observation",
            "label": "Write one detailed observation",
            "type": "text",
            "placeholder": "When the contact closed...",
            "min_length": 24
          }
        ]
      },
      {
        "id": "wind-chain",
        "type": "sequence_builder",
        "title": "Build the observed Wind Engine chain",
        "prompt": "Use evidence from your actual first run.",
        "save_as": "observed_wind_chain",
        "items": [
          {
            "id": "battery",
            "label": "Stored chemical energy in two AA batteries"
          },
          {
            "id": "electrical",
            "label": "Energy transferred electrically through the closed circuit"
          },
          {
            "id": "motor",
            "label": "Motor shaft rotates"
          },
          {
            "id": "blade",
            "label": "Fixed angled blade rotates"
          },
          {
            "id": "air",
            "label": "Blade pushes and moves existing room air"
          }
        ],
        "correct_order": [
          "battery",
          "electrical",
          "motor",
          "blade",
          "air"
        ],
        "correct_feedback": "Correct. The motor rotation happens before the blade can move air.",
        "retry_feedback": "Trace the causal chain from battery to airflow."
      },
      {
        "id": "learned-pwo",
        "type": "pwo_asset",
        "title": "Open the PWO science summary",
        "mission": "Compare the PWO explanation with your physical evidence. Return to the Journey and correct any mismatch in your own words.",
        "save_as": "learned_pwo_opened",
        "asset": {
          "id": "learned-pwo-asset",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1171,
            "scene_name": "i_learned"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 169,
            "collection_item_id": 148
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 936,
            "source_topic_id": 961
          }
        }
      },
      {
        "id": "step7-saras",
        "type": "saras_checkpoint",
        "title": "Analyze the first Wind Engine run with Saras",
        "suggested_questions": [
          "Ask me for one piece of visual, sound, and airflow evidence.",
          "Does my energy chain match the order of events?",
          "Why does the blade move existing air rather than create air?",
          "What does a wobble suggest compared with complete silence?"
        ],
        "hidden_context": "The learner completed the first powered test of the two-AA Electric Spiral kit. Use their visual/sound/air evidence. Accurate chain: battery chemical energy -> electrical transfer in closed circuit -> motor rotation -> angled blade rotation -> moving existing air. Distinguish electrical and mechanical symptoms. Safety: no powered adjustments.",
        "reveal_policy": "The complete motor-airflow chain may be discussed. Preserve Step 8 as a controlled direction investigation if the result was unexpected."
      },
      {
        "id": "step7-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "The Wind Engine is awake",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Your evidence is ready. Continue to the next Breeze Station challenge.",
        "rules": [
          {
            "field": "first_test_image",
            "condition": "answered"
          },
          {
            "field": "first_run_prediction",
            "condition": "equals",
            "value": "motor_then_air"
          },
          {
            "field": "first_run_checklist",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "first_run_record",
            "condition": "record_complete"
          },
          {
            "field": "observed_wind_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "learned_pwo_opened",
            "condition": "truthy"
          }
        ]
      }
    ]
  },
  "backward-breeze-mystery": {
    "step_id": "backward-breeze-mystery",
    "title": "The Backward Breeze Mystery",
    "subtitle": "Change One Variable: Motor-Lead Direction",
    "phase": "Investigate",
    "story": "The motor may spin while the strongest breeze appears on the wrong side. The team must test whether changing only the motor-lead orientation reverses rotation and airflow.",
    "blocks": [
      {
        "id": "reverse-story",
        "type": "story_hook",
        "eyebrow": "DIRECTION MYSTERY",
        "title": "The machine is awake—but is the breeze going where you expected?",
        "body": "Make a prediction before changing anything. Then power off, change only the two motor leads, and repeat the same ribbon test with teacher approval.",
        "tone": "mystery"
      },
      {
        "id": "reverse-gallery",
        "type": "image_gallery",
        "title": "Study the controlled comparison",
        "prompt": "Everything except motor-lead orientation should stay the same.",
        "save_as": "direction_diagram_examined",
        "images": [
          {
            "id": "reverse",
            "label": "Test 1 vs Test 2",
            "path": "electric-spiral/step-8/backward_breeze_mystery.png",
            "caption": "Swapping the leads reverses current through the DC motor. The battery chemistry and blade shape do not reverse.",
            "alt": "A comparison of two motor wiring orientations and opposite airflow directions."
          }
        ]
      },
      {
        "id": "reverse-prediction",
        "type": "choice_cards",
        "title": "What should change after swapping motor leads?",
        "prompt": "Assume batteries, blade, structure, and ribbon position stay the same.",
        "choices": [
          {
            "id": "rotation_air",
            "label": "Motor rotation reverses, so the airflow direction changes"
          },
          {
            "id": "battery_chemistry",
            "label": "Battery chemistry reverses"
          },
          {
            "id": "blade_shape",
            "label": "The blade physically changes its angle"
          },
          {
            "id": "more_energy",
            "label": "The batteries automatically supply more energy"
          }
        ],
        "save_as": "reverse_prediction",
        "required": true
      },
      {
        "id": "fair-test-plan",
        "type": "classification",
        "title": "Keep the test fair",
        "prompt": "Classify each factor as the changed variable or something to hold constant.",
        "categories": [
          {
            "id": "change",
            "label": "Change deliberately"
          },
          {
            "id": "constant",
            "label": "Hold constant"
          }
        ],
        "items": [
          {
            "id": "lead_orientation",
            "label": "Which motor terminal receives red versus black"
          },
          {
            "id": "battery_set",
            "label": "The same two AA batteries"
          },
          {
            "id": "blade",
            "label": "The same fixed blade"
          },
          {
            "id": "structure",
            "label": "The same tower and motor position"
          },
          {
            "id": "ribbon_position",
            "label": "The same tissue/ribbon location and distance"
          },
          {
            "id": "test_time",
            "label": "The same brief observation time"
          }
        ],
        "save_as": "direction_fair_test",
        "required": true
      },
      {
        "id": "direction-safety",
        "type": "build_checklist",
        "title": "Power-off direction-test gate",
        "prompt": "The motor leads are moved only while power is off.",
        "save_as": "direction_safety_gate",
        "statuses": [
          {
            "id": "complete",
            "label": "Checked"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "open_power",
            "label": "Open the battery-holder contact before touching leads"
          },
          {
            "id": "stop_blade",
            "label": "Wait until the blade is completely still"
          },
          {
            "id": "teacher_swap",
            "label": "Teacher approves or assists with swapping the two motor leads"
          },
          {
            "id": "same_test",
            "label": "Return the ribbon/tissue to the same safe position"
          },
          {
            "id": "brief_retest",
            "label": "Close power briefly, observe, then open power again"
          }
        ]
      },
      {
        "id": "direction-record",
        "type": "experiment_record",
        "title": "Compare Test 1 and Test 2",
        "prompt": "Record what changed and what stayed the same.",
        "save_as": "direction_test_record",
        "fields": [
          {
            "id": "test1",
            "label": "Test 1 airflow evidence",
            "type": "text",
            "placeholder": "Before swapping leads...",
            "min_length": 18
          },
          {
            "id": "test2",
            "label": "Test 2 airflow evidence",
            "type": "text",
            "placeholder": "After swapping leads...",
            "min_length": 18
          },
          {
            "id": "conclusion",
            "label": "One-variable conclusion",
            "type": "text",
            "placeholder": "Because only... changed, the evidence supports...",
            "min_length": 35
          }
        ]
      },
      {
        "id": "reverse-check",
        "type": "quiz_question",
        "title": "Direction mystery check",
        "prompt": "What actually reverses when red and black motor leads are swapped?",
        "choices": [
          {
            "id": "current_motor",
            "label": "The direction of current through the motor, reversing shaft rotation"
          },
          {
            "id": "chemistry",
            "label": "The chemical reaction inside each AA battery"
          },
          {
            "id": "air_exists",
            "label": "The existence of air in the room"
          },
          {
            "id": "blade_pitch",
            "label": "The fixed blade's molded angle"
          }
        ],
        "correct_answer": "current_motor",
        "save_as": "direction_science_check",
        "correct_feedback": "Correct. Motor current direction reverses, so the shaft and blade rotate the other way.",
        "incorrect_feedback": "Identify what changed at the motor terminals—not inside the batteries or molded blade."
      },
      {
        "id": "direction-video",
        "type": "video_asset",
        "title": "Optional enrichment: The Power vs Direction Paradox",
        "mission": "Watch only after completing the physical prediction and comparison. Focus on the one changed variable; ignore unnecessary advanced motor vocabulary.",
        "save_as": "direction_video_opened",
        "asset": {
          "id": "direction-video-asset",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7057,
            "tag_id": 30074
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 169,
            "collection_item_id": 148
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 936,
            "source_topic_id": 961
          }
        }
      },
      {
        "id": "step8-saras",
        "type": "saras_checkpoint",
        "title": "Solve the backward breeze with Saras",
        "suggested_questions": [
          "Ask me what changed and what stayed constant.",
          "Why does swapping motor leads reverse rotation?",
          "Check whether my conclusion overclaims the evidence.",
          "Help me distinguish battery polarity from motor-current direction."
        ],
        "hidden_context": "The learner performed or planned a teacher-approved power-off swap of the two DC motor leads. Accurate: swapping leads reverses current through the motor, reversing shaft and blade rotation; fixed angled blade then changes airflow direction. Batteries, blade shape, and test position stay constant. Coach one-variable reasoning and safety.",
        "reveal_policy": "The direction mechanism may now be explained. Do not suggest powered lead changes."
      },
      {
        "id": "step8-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Backward breeze mystery solved",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Your evidence is ready. Continue to the next Breeze Station challenge.",
        "rules": [
          {
            "field": "direction_diagram_examined",
            "condition": "answered"
          },
          {
            "field": "reverse_prediction",
            "condition": "equals",
            "value": "rotation_air"
          },
          {
            "field": "direction_fair_test",
            "condition": "completed"
          },
          {
            "field": "direction_safety_gate",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "direction_test_record",
            "condition": "record_complete"
          },
          {
            "field": "direction_science_check",
            "condition": "equals",
            "value": "current_motor"
          }
        ]
      }
    ]
  },
  "rescue-the-engine": {
    "step_id": "rescue-the-engine",
    "title": "Rescue the Shaky or Silent Engine",
    "subtitle": "Diagnose Before You Rebuild",
    "phase": "Engineer",
    "story": "Breeze Station inspectors introduce two fault cases: a silent machine and a wobbling machine. The team must distinguish electrical faults from mechanical faults using ordered evidence.",
    "blocks": [
      {
        "id": "rescue-story",
        "type": "story_hook",
        "eyebrow": "FINAL ENGINEERING INSPECTION",
        "title": "Silent and shaky are different clues",
        "body": "Do not change three things at once. Power off, choose a hypothesis, check one cause, and retest using the same ribbon position.",
        "tone": "mystery"
      },
      {
        "id": "troubleshoot-gallery",
        "type": "image_gallery",
        "title": "Use the five-check rescue flow",
        "prompt": "Trace the order before choosing a fault case.",
        "save_as": "troubleshooting_flow_examined",
        "images": [
          {
            "id": "flow",
            "label": "Wind Engine rescue flow",
            "path": "electric-spiral/step-9/troubleshooting_flow.png",
            "caption": "Safety, power, contacts, mechanical alignment, then one deliberate change and retest.",
            "alt": "A five-step troubleshooting flow for a silent or shaky Electric Spiral fan."
          }
        ]
      },
      {
        "id": "fault-classification",
        "type": "classification",
        "title": "Electrical or mechanical clue?",
        "prompt": "Classify the strongest first interpretation. Some real faults can overlap, so use this as a starting hypothesis.",
        "categories": [
          {
            "id": "electrical",
            "label": "Likely electrical path/contact"
          },
          {
            "id": "mechanical",
            "label": "Likely structure/blade/alignment"
          },
          {
            "id": "either",
            "label": "Could be either—need another check"
          }
        ],
        "items": [
          {
            "id": "silent_no_motion",
            "label": "No motor sound and no blade motion"
          },
          {
            "id": "motor_sound_still",
            "label": "Motor sound is heard but blade does not rotate"
          },
          {
            "id": "wobble",
            "label": "Blade rotates but wobbles strongly"
          },
          {
            "id": "intermittent",
            "label": "Motor starts only when a wire is moved"
          },
          {
            "id": "tower_lean",
            "label": "Tower leans and the blade rubs a support"
          }
        ],
        "save_as": "fault_classification",
        "required": true
      },
      {
        "id": "diagnostic-sequence",
        "type": "sequence_builder",
        "title": "Order the safe diagnostic checks",
        "prompt": "Start with safety, then distinguish power/contact from mechanical alignment.",
        "save_as": "diagnostic_sequence",
        "items": [
          {
            "id": "power_off",
            "label": "Open power and wait for the blade to stop"
          },
          {
            "id": "battery_switch",
            "label": "Check battery orientation and holder contact/switch"
          },
          {
            "id": "contacts",
            "label": "Check metal-to-metal lead contact on motor terminals"
          },
          {
            "id": "mechanical",
            "label": "Inspect motor mount, blade fit, clearance, and base stability"
          },
          {
            "id": "one_change",
            "label": "Make one safe change"
          },
          {
            "id": "retest",
            "label": "Repeat the same brief ribbon/tissue test"
          }
        ],
        "correct_order": [
          "power_off",
          "battery_switch",
          "contacts",
          "mechanical",
          "one_change",
          "retest"
        ],
        "correct_feedback": "Correct. This order protects the learner and preserves evidence.",
        "retry_feedback": "Begin with power off, then move from electrical checks to mechanical checks before one retest."
      },
      {
        "id": "trial1-summary",
        "type": "prior_work_summary",
        "title": "Review your first-run evidence",
        "prompt": "Use the actual record instead of memory.",
        "items": [
          {
            "source_step_id": "wake-the-wind-engine",
            "field": "first_run_record",
            "label": "First run",
            "field_labels": {
              "visual": "Visual",
              "sound": "Sound",
              "air": "Air",
              "observation": "Observation"
            },
            "value_labels": {
              "steady": "Blade spun steadily",
              "wobble": "Blade wobbled",
              "still": "Blade stayed still",
              "steady_hum": "Steady motor sound",
              "intermittent": "Intermittent sound",
              "silent": "No sound",
              "away": "Ribbon moved away",
              "toward": "Ribbon moved toward machine / opposite airflow",
              "none": "No clear airflow"
            }
          },
          {
            "source_step_id": "build-the-breeze-tower",
            "field": "assembly_weak_point",
            "label": "Predicted weak point"
          }
        ]
      },
      {
        "id": "fault-hypothesis",
        "type": "choice_cards",
        "title": "Choose the strongest current hypothesis",
        "prompt": "Base the choice on your first-run evidence.",
        "choices": [
          {
            "id": "power_path",
            "label": "Battery orientation, switch/contact, or motor terminal connection"
          },
          {
            "id": "blade_fit",
            "label": "Loose/off-center blade or blocked rotation"
          },
          {
            "id": "structure",
            "label": "Unstable tower or blade rubbing the frame"
          },
          {
            "id": "direction_only",
            "label": "Machine works, but airflow direction needs the Step 8 lead swap"
          },
          {
            "id": "need_one_check",
            "label": "Evidence is mixed; I need one diagnostic check"
          }
        ],
        "save_as": "fault_hypothesis",
        "required": true
      },
      {
        "id": "redesign-record",
        "type": "experiment_record",
        "title": "Record one change and two trials",
        "prompt": "Accept a negative redesign result if the evidence is honest.",
        "save_as": "rescue_trial_record",
        "fields": [
          {
            "id": "changed_variable",
            "label": "One variable changed",
            "type": "text",
            "placeholder": "I changed only...",
            "min_length": 15
          },
          {
            "id": "trial1",
            "label": "Trial 1 evidence",
            "type": "text",
            "placeholder": "Before the change...",
            "min_length": 22
          },
          {
            "id": "trial2",
            "label": "Trial 2 evidence",
            "type": "text",
            "placeholder": "After the change...",
            "min_length": 22
          },
          {
            "id": "judgment",
            "label": "Did reliability improve? Why?",
            "type": "text",
            "placeholder": "The evidence shows...",
            "min_length": 35
          }
        ]
      },
      {
        "id": "diagnostics-video",
        "type": "video_asset",
        "title": "Optional expert evidence: Circuit Diagnostics",
        "mission": "Watch after choosing your hypothesis. Compare the video's diagnostic order with your own and reject any unsafe shortcut.",
        "save_as": "diagnostics_video_opened",
        "asset": {
          "id": "diagnostics-video-asset",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7056,
            "tag_id": 30073
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 169,
            "collection_item_id": 148
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 936,
            "source_topic_id": 961
          }
        }
      },
      {
        "id": "safety-quiz",
        "type": "quiz_question",
        "title": "Rescue safety check",
        "prompt": "A lead appears loose while the blade is spinning. What should happen first?",
        "choices": [
          {
            "id": "power_off_wait",
            "label": "Open power and wait for the blade to stop before touching the lead"
          },
          {
            "id": "grab_wire",
            "label": "Grab the lead while powered"
          },
          {
            "id": "hold_blade",
            "label": "Stop the blade with a finger"
          },
          {
            "id": "short_test",
            "label": "Touch red and black leads together"
          }
        ],
        "correct_answer": "power_off_wait",
        "save_as": "rescue_safety_check",
        "correct_feedback": "Correct. Power off and wait for all motion to stop before adjustment.",
        "incorrect_feedback": "Never touch wiring or stop the blade while powered."
      },
      {
        "id": "step9-saras",
        "type": "saras_checkpoint",
        "title": "Diagnose the Wind Engine with Saras",
        "suggested_questions": [
          "Give me one diagnostic check at a time.",
          "Does my evidence point more strongly to an electrical or mechanical fault?",
          "Did I change only one variable?",
          "Challenge my reliability claim without rewriting it."
        ],
        "hidden_context": "The learner is diagnosing a two-AA Electric Spiral fan. Use prior first_run_record, assembly_weak_point, direction_test_record, fault_hypothesis, and rescue_trial_record. Electrical clues: no sound/motion, intermittent contact. Mechanical clues: motor sound without blade movement, wobble, rubbing, leaning. Safety: power off before touching leads/blade; no direct short. Coach one check at a time.",
        "reveal_policy": "All relevant science may be discussed. Preserve evidence-based diagnosis and do not author the learner's conclusion."
      },
      {
        "id": "step9-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Wind Engine rescued with evidence",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Your evidence is ready. Continue to the next Breeze Station challenge.",
        "rules": [
          {
            "field": "troubleshooting_flow_examined",
            "condition": "answered"
          },
          {
            "field": "fault_classification",
            "condition": "completed"
          },
          {
            "field": "diagnostic_sequence",
            "condition": "sequence_correct"
          },
          {
            "field": "fault_hypothesis",
            "condition": "answered"
          },
          {
            "field": "rescue_trial_record",
            "condition": "record_complete"
          },
          {
            "field": "rescue_safety_check",
            "condition": "equals",
            "value": "power_off_wait"
          }
        ]
      }
    ]
  },
  "wind-engine-report": {
    "step_id": "wind-engine-report",
    "title": "Reveal the Secret of the Sleeping Wind Engine",
    "subtitle": "Day 1 Engineering Report",
    "phase": "Reflect",
    "story": "Breeze Station can open only when the new engineers explain why the signal beacon and Wind Engine both work, how direction changed, and which trial evidence proves reliability.",
    "blocks": [
      {
        "id": "final-story",
        "type": "story_hook",
        "eyebrow": "BREEZE STATION REOPENING REVIEW",
        "title": "The machines are awake—now prove why",
        "body": "A final report must connect Part A, Part B, safe testing, and evidence. It should not be a list of assembly steps.",
        "tone": "mystery"
      },
      {
        "id": "final-gallery",
        "type": "image_gallery",
        "title": "Review the restored station and two-machine chain",
        "prompt": "Use these visuals only as evidence prompts; write the report in your own words.",
        "save_as": "final_evidence_image",
        "images": [
          {
            "id": "chain",
            "label": "Final two-machine chain",
            "path": "electric-spiral/step-10/final_two_machine_chain.png",
            "caption": "Both machines need a complete conducting loop. Their output devices create different results.",
            "alt": "Causal chains for the signal beacon and Wind Engine."
          },
          {
            "id": "restored",
            "label": "Breeze Station restored",
            "path": "electric-spiral/step-10/breeze_station_restored.png",
            "caption": "The Day 1 mission ends with the beacon lit, Wind Engine awake, and evidence recorded.",
            "alt": "A restored Breeze Station with lit beacons and the Electric Spiral fan."
          }
        ]
      },
      {
        "id": "prior-evidence",
        "type": "prior_work_summary",
        "title": "Bring forward your strongest evidence",
        "prompt": "The final report should use records from both builds and the direction/redesign investigations.",
        "items": [
          {
            "source_step_id": "light-the-signal-beacon",
            "field": "parta_trial",
            "label": "Part A signal-beacon trial",
            "field_labels": {
              "first_result": "Result",
              "evidence": "Evidence",
              "fix": "Fix"
            },
            "value_labels": {
              "lit": "Lamp lit",
              "dark": "Lamp stayed dark",
              "flicker": "Lamp flickered"
            }
          },
          {
            "source_step_id": "wake-the-wind-engine",
            "field": "first_run_record",
            "label": "Part B first run",
            "field_labels": {
              "visual": "Visual",
              "sound": "Sound",
              "air": "Air",
              "observation": "Observation"
            }
          },
          {
            "source_step_id": "backward-breeze-mystery",
            "field": "direction_test_record",
            "label": "Direction comparison",
            "field_labels": {
              "test1": "Test 1",
              "test2": "Test 2",
              "conclusion": "Conclusion"
            }
          },
          {
            "source_step_id": "rescue-the-engine",
            "field": "rescue_trial_record",
            "label": "Reliability redesign",
            "field_labels": {
              "changed_variable": "Changed variable",
              "trial1": "Trial 1",
              "trial2": "Trial 2",
              "judgment": "Judgment"
            }
          }
        ]
      },
      {
        "id": "final-chain",
        "type": "sequence_builder",
        "title": "Build the final Wind Engine causal chain",
        "prompt": "Order the energy transfer and physical events.",
        "save_as": "final_wind_chain",
        "items": [
          {
            "id": "chem",
            "label": "Stored chemical energy in two AA batteries"
          },
          {
            "id": "electrical",
            "label": "Energy transferred electrically through a closed conducting loop"
          },
          {
            "id": "motor",
            "label": "DC motor shaft rotates"
          },
          {
            "id": "blade",
            "label": "Fixed angled blade rotates"
          },
          {
            "id": "air",
            "label": "Blade pushes and moves existing air"
          }
        ],
        "correct_order": [
          "chem",
          "electrical",
          "motor",
          "blade",
          "air"
        ],
        "correct_feedback": "Correct. This is the complete causal chain.",
        "retry_feedback": "Separate the electrical transfer, motor rotation, blade rotation, and moving air."
      },
      {
        "id": "final-honesty",
        "type": "quiz_question",
        "title": "Final science honesty check",
        "prompt": "Which statement best compares Part A and Part B?",
        "choices": [
          {
            "id": "same_rule_diff_output",
            "label": "Both need a complete conducting loop; the lamp produces light, while the motor and blade produce rotation and moving air"
          },
          {
            "id": "air_makes_power",
            "label": "The fan's air creates the battery energy"
          },
          {
            "id": "energy_returns",
            "label": "Energy travels around the wires and returns unchanged to the battery"
          },
          {
            "id": "parta_unrelated",
            "label": "The signal beacon and Wind Engine use unrelated electrical rules"
          }
        ],
        "correct_answer": "same_rule_diff_output",
        "save_as": "final_science_check",
        "correct_feedback": "Correct. Same circuit rule, different output device and result.",
        "incorrect_feedback": "Use evidence from both builds and keep current path separate from energy transfer."
      },
      {
        "id": "engineer-report",
        "type": "experiment_record",
        "title": "Deliver the Breeze Engineer Report",
        "prompt": "Answer all five questions with causal language and evidence.",
        "save_as": "breeze_engineer_report",
        "fields": [
          {
            "id": "shared_rule",
            "label": "1. What hidden circuit rule did both machines obey?",
            "type": "text",
            "placeholder": "Both machines needed...",
            "min_length": 28
          },
          {
            "id": "parta",
            "label": "2. How did Part A prove the rule?",
            "type": "text",
            "placeholder": "The signal beacon showed...",
            "min_length": 28
          },
          {
            "id": "partb",
            "label": "3. How did electrical input lead to moving air in Part B?",
            "type": "text",
            "placeholder": "The batteries... then the motor...",
            "min_length": 40
          },
          {
            "id": "direction",
            "label": "4. Why did swapping motor leads change airflow direction?",
            "type": "text",
            "placeholder": "Swapping the leads reversed...",
            "min_length": 32
          },
          {
            "id": "evidence",
            "label": "5. Which two observations best support your reliability claim?",
            "type": "text",
            "placeholder": "Trial 1 showed... Trial 2 showed...",
            "min_length": 42
          }
        ]
      },
      {
        "id": "final-reflection",
        "type": "evidence_response",
        "title": "Reflect on Day 1",
        "prompt": "What changed from your opening theory? What are you proud of, and what would you investigate next?",
        "placeholder": "At first I thought... Now I know... I am proud that... Next I would test...",
        "save_as": "final_reflection",
        "min_length": 45,
        "required": true
      },
      {
        "id": "step10-saras",
        "type": "saras_checkpoint",
        "title": "Rehearse the Breeze Engineer report with Saras",
        "suggested_questions": [
          "Ask me one final-report question at a time.",
          "Does my explanation use evidence from both Part A and Part B?",
          "Did I distinguish current path from energy transformation?",
          "Help me remove an inaccurate claim without rewriting my answer."
        ],
        "hidden_context": "The learner is completing The Mystery of the Sleeping Wind Engine. Use all saved records. Accurate core: Part A one-AA lamp circuit and Part B two-AA motor/fan circuit both require closed conducting loops; batteries transfer stored chemical energy electrically; lamp produces light; motor produces rotation; fixed angled blade moves existing air; swapping motor leads reverses current through motor and reverses rotation/airflow; structure and contacts affect reliability. Coach one question at a time and preserve learner wording.",
        "reveal_policy": "The complete science may be discussed. Coach, do not author the final report."
      },
      {
        "id": "step10-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish this engineering checkpoint",
        "complete_title": "Journey complete: Breeze Engineer",
        "incomplete_prompt": "Your work is saved. Complete the remaining evidence before moving on.",
        "complete_prompt": "Breeze Station is restored. Day 1 engineering evidence is complete.",
        "rules": [
          {
            "field": "final_evidence_image",
            "condition": "answered"
          },
          {
            "field": "final_wind_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "final_science_check",
            "condition": "equals",
            "value": "same_rule_diff_output"
          },
          {
            "field": "breeze_engineer_report",
            "condition": "record_complete"
          },
          {
            "field": "final_reflection",
            "condition": "min_length",
            "value": 45
          }
        ]
      }
    ]
  }
};

export const getElectricSpiralJourneyPage = (stepId) =>
  ELECTRIC_SPIRAL_STEP_PAGES[String(stepId || "")] || null;

export default ELECTRIC_SPIRAL_STEP_PAGES;
